/**
 * Google Analytics 4 - Event Tracking for Agathos Website
 *
 * GA4 Event Map:
 * ┌─────────────────────┬──────────────────────────────────────────────┐
 * │ Event               │ Trigger                                      │
 * ├─────────────────────┼──────────────────────────────────────────────┤
 * │ page_view           │ Automatic (GA4 enhanced measurement)         │
 * │ cta_click           │ Hero CTA, "Let's Talk", "See more" buttons   │
 * │ form_start          │ First interaction with contact form           │
 * │ form_submit         │ Successful contact form submission            │
 * │ form_error          │ Contact form submission error                 │
 * │ service_expand      │ User expands a service card                   │
 * │ project_view        │ User switches project tab                     │
 * │ language_switch     │ User changes language                         │
 * │ hero_slide          │ User navigates hero carousel                  │
 * │ social_click        │ User clicks a social media link               │
 * └─────────────────────┴──────────────────────────────────────────────┘
 *
 * KPI Dashboard Recommendations for Founders:
 *
 * 1. Acquisition:   Sessions by source/medium, organic vs direct vs referral
 * 2. Engagement:    Avg. engagement time, bounce rate, pages per session
 * 3. Conversions:   form_submit count (primary KPI), form_start → form_submit rate
 * 4. Content:       service_expand counts (which services get attention)
 * 5. Portfolio:     project_view by project name (which projects attract interest)
 * 6. Audience:      language_switch ratio (demand for ES vs EN)
 * 7. UX:            hero_slide interactions, cta_click by label
 */

type GTagEvent = {
  cta_click: {
    cta_label: string;
    cta_location: string;
    cta_url?: string;
  };
  form_start: {
    form_id: string;
  };
  form_submit: {
    form_id: string;
  };
  form_error: {
    form_id: string;
    error_type: string;
  };
  service_expand: {
    service_name: string;
  };
  project_view: {
    project_name: string;
    project_type?: string;
  };
  language_switch: {
    from_language: string;
    to_language: string;
  };
  hero_slide: {
    slide_index: number;
    slide_direction: 'next' | 'prev' | 'dot';
  };
  social_click: {
    platform: string;
    link_url: string;
  };
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export function trackEvent<T extends keyof GTagEvent>(
  event: T,
  params: GTagEvent[T]
) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', event, params);
  }
}

export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
